import React, { useState } from "react";
import { ShieldAlert, ShieldCheck, ShieldOff, Info, AlertTriangle, CheckCircle2, KeyRound, Crown, UserX, ArrowRight, ChevronRight, Wrench } from "lucide-react";
import { toast } from "sonner";
import { buildRemediation } from "../../lib/remediation";
import usePlannerDataset from "../../hooks/usePlannerDataset";
import UserDrillSheet from "./UserDrillSheet";

const LEVEL = {
  crit: { icon: ShieldAlert, label: "Critical" },
  warn: { icon: AlertTriangle, label: "Warning" },
  info: { icon: Info, label: "Info" },
  ok: { icon: CheckCircle2, label: "OK" },
};
const fmt = (iso) => (iso ? new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" }) : "—");

/** Which user list a check drills into (null = not drillable). */
const drillFor = (ch, sec) => {
  const d = (filter, title, description) => ({ title, description, params: { filter } });
  switch (ch.id) {
    case "admins_mfa":
      if (!sec.available.registration) return null;
      return ch.count ? d("admins_nomfa", ch.title, "Active privileged role holders with no MFA method registered. Enforce MFA for directory roles with Conditional Access.") : d("admins", ch.title, "All active admin role holders and their MFA registration state.");
    case "admins_p2":
      return ch.count ? d("admins_nop2", ch.title, "Privileged users whose licenses do not include Entra ID P2 (PIM, Identity Protection).") : d("admins", ch.title, "All active admin role holders hold Entra ID P2.");
    case "global_admins":
      return d("global_admins", ch.title, "Active accounts holding the Global Administrator role.");
    case "admins_guest":
      return d("guest_admins", ch.title, "Guest (external) accounts holding privileged directory roles.");
    case "legacy_auth":
      return ch.count ? d("legacy", ch.title, `Users who signed in with basic-auth protocols in the last ${sec.legacy_auth?.window_days || 29} days.`) : null;
    case "mfa_coverage":
      return ch.count ? d("nomfa", ch.title, "Active member accounts that have not registered any MFA method.") : null;
    case "block_legacy":
      return { navigate: "ca" };
    default:
      return null;
  }
};

export default function SecurityPosture({ summary, scanId, onShowUsers, onShowCa, onPlanFix, isAdmin }) {
  const sec = summary.security;
  const [adminFilter, setAdminFilter] = useState("all");
  const [drill, setDrill] = useState(null);
  const { dataset, plans, addons, rows } = usePlannerDataset();
  const canFix = isAdmin && !!onPlanFix && !!dataset;
  const planFix = (fix) => {
    const groups = (sec.remediation || []).filter((g) => g.finding === fix.finding);
    const { moves, notes } = buildRemediation({ gaps: { remediation: groups }, plans, addons, rows, priceMode: "teams" });
    if (!moves.length) return toast.error("No priced fix found", { description: notes.join("; ") || "No plan or add-on in the dataset covers this requirement." });
    notes.forEach((n) => toast.warning(n));
    setDrill(null);
    onPlanFix(moves.map((m) => ({ ...m, remediation: { ...m.remediation, finding: fix.finding, label: fix.label } })), `Planner pre-filled: ${fix.label}`);
    return undefined;
  };
  const fixButton = (ch, size = "sm") =>
    canFix && ch.fix?.users > 0 ? (
      <button type="button" className={`tb-btn ${size}`} onClick={(e) => (e.stopPropagation(), planFix(ch.fix))} data-testid={`check-fix-${ch.id}`} title={ch.fix.why}>
        <Wrench size={12} /> Plan fix · {ch.fix.users} user{ch.fix.users === 1 ? "" : "s"}
      </button>
    ) : null;
  const openCheck = (ch) => {
    const d = drillFor(ch, sec);
    if (!d) return;
    if (d.navigate === "ca") onShowCa?.();
    else setDrill(canFix && ch.fix?.users > 0 ? { ...d, fix: { label: `Plan fix: ${ch.fix.label}`, run: () => planFix(ch.fix) } } : d);
  };
  if (!sec) {
    return (
      <section className="ws-card glass" data-testid="security-unavailable">
        <h2>
          <ShieldOff size={16} /> Security posture
        </h2>
        <div className="ws-alert">
          <Info size={14} />{" "}
          {summary.source === "csv"
            ? "This tenant was imported from a CSV export. Security posture (admin roles, MFA registration, legacy-auth sign-ins) needs a Microsoft Graph connection with Directory.Read.All and AuditLog.Read.All."
            : "This scan predates the security checks - run a new scan. For real tenants grant Directory.Read.All (roles, sign-in logs) and AuditLog.Read.All (MFA registration) to the app registration."}
        </div>
      </section>
    );
  }
  const admins = sec.admins.filter((a) => (adminFilter === "nomfa" ? a.mfa_registered === false : adminFilter === "nop2" ? !a.entra_p2 : adminFilter === "global" ? a.global_admin : true));
  const c = sec.admin_counts;
  return (
    <div className="ws-stack" data-testid="security-posture">
      <div className="kpi-grid security">
        <div className={`kpi glass ${sec.score.crit ? "crit" : sec.score.warn ? "warn" : "ok"}`} data-testid="security-score">
          <span className="kpi-ico">{sec.score.crit ? <ShieldAlert size={16} /> : <ShieldCheck size={16} />}</span>
          <div>
            <div className="kpi-value mono">
              {sec.score.crit} <span className="kpi-unit">critical</span> · {sec.score.warn} <span className="kpi-unit">warnings</span>
            </div>
            <div className="kpi-label">Security checks</div>
            <div className="kpi-sub">{sec.score.ok} passed</div>
          </div>
        </div>
        <div className={`kpi glass ${c.no_mfa ? "crit" : ""} ${c.total ? "clickable" : ""}`} onClick={() => c.total && setDrill({ title: `${c.total} admin role holders`, description: "Every account holding a privileged directory role, with MFA registration, Entra ID plan and licenses.", params: { filter: "admins" } })} data-testid="security-admins">
          <span className="kpi-ico">
            <Crown size={16} />
          </span>
          <div>
            <div className="kpi-value mono">{c.total}</div>
            <div className="kpi-label">Admin role holders</div>
            <div className="kpi-sub">
              {c.global} Global Admin{c.global === 1 ? "" : "s"} · {c.no_mfa} without MFA · {c.no_p2} without Entra P2
            </div>
            {c.total > 0 && (
              <span className="kpi-link">
                View users <ChevronRight size={12} />
              </span>
            )}
          </div>
        </div>
        <div className={`kpi glass ${sec.mfa && sec.mfa.coverage < 90 ? "warn" : ""} ${sec.mfa?.not_registered ? "clickable" : ""}`} onClick={() => sec.mfa?.not_registered && setDrill({ title: `${sec.mfa.not_registered.toLocaleString()} active members without MFA`, description: "Member accounts that have not registered any MFA method - target them with a registration campaign.", params: { filter: "nomfa" } })} data-testid="security-mfa">
          <span className="kpi-ico">
            <KeyRound size={16} />
          </span>
          <div>
            <div className="kpi-value mono">{sec.mfa ? `${sec.mfa.coverage ?? 0}%` : "n/a"}</div>
            <div className="kpi-label">MFA registered (active members)</div>
            <div className="kpi-sub">{sec.mfa ? `${sec.mfa.registered.toLocaleString()} of ${sec.mfa.total.toLocaleString()} · ${sec.mfa.not_registered.toLocaleString()} missing` : sec.errors.registration || "AuditLog.Read.All required"}</div>
            {sec.mfa?.not_registered > 0 && (
              <span className="kpi-link">
                View users <ChevronRight size={12} />
              </span>
            )}
          </div>
        </div>
        <div className={`kpi glass ${sec.legacy_auth?.sign_ins ? "warn clickable" : ""}`} onClick={() => sec.legacy_auth?.sign_ins && setDrill({ title: `${sec.legacy_auth.users} user${sec.legacy_auth.users === 1 ? "" : "s"} using legacy authentication`, description: `Basic-auth sign-ins (IMAP, POP, SMTP AUTH, ActiveSync...) in the last ${sec.legacy_auth.window_days} days.`, params: { filter: "legacy" } })} data-testid="security-legacy">
          <span className="kpi-ico">
            <UserX size={16} />
          </span>
          <div>
            <div className="kpi-value mono">{sec.legacy_auth ? sec.legacy_auth.sign_ins.toLocaleString() : "n/a"}</div>
            <div className="kpi-label">Legacy-auth sign-ins ({sec.legacy_auth?.window_days || 29}d)</div>
            <div className="kpi-sub">{sec.legacy_auth ? `${sec.legacy_auth.users} user${sec.legacy_auth.users === 1 ? "" : "s"} · ${sec.legacy_auth.successful} successful` : sec.errors.signins || "Needs Entra ID P1 + AuditLog.Read.All"}</div>
            {sec.legacy_auth?.sign_ins > 0 && (
              <span className="kpi-link">
                View users <ChevronRight size={12} />
              </span>
            )}
          </div>
        </div>
      </div>

      <section className="ws-card glass">
        <div className="ws-card-head">
          <h2>Checks</h2>
          <span className="muted small">Click a check to see the affected users or policy.</span>
        </div>
        <ul className="check-list" data-testid="security-checks">
          {sec.checks.map((ch) => {
            const L = LEVEL[ch.level] || LEVEL.info;
            const d = drillFor(ch, sec);
            return (
              <li key={ch.id} className={`check ${ch.level} ${d ? "drillable" : ""}`} role={d ? "button" : undefined} tabIndex={d ? 0 : undefined} onClick={() => openCheck(ch)} onKeyDown={(e) => d && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), openCheck(ch))} data-testid={`check-${ch.id}`}>
                <L.icon size={16} />
                <div>
                  <b>{ch.title}</b>
                  <p>{ch.detail}</p>
                  {ch.fix?.users > 0 && (
                    <p className="check-fix-note" data-testid={`check-fix-note-${ch.id}`}>
                      <Wrench size={11} /> {ch.fix.users} of them lack the license for the fix ({ch.fix.label}). {ch.fix.why}
                    </p>
                  )}
                </div>
                {fixButton(ch)}
                <span className={`mini-chip ${ch.level === "ok" ? "ok" : ch.level === "info" ? "" : "warn"}`}>{L.label}</span>
                {d && (
                  <span className="check-cta" data-testid={`check-drill-${ch.id}`}>
                    {d.navigate ? "View policies" : "View users"} <ChevronRight size={13} />
                  </span>
                )}
              </li>
            );
          })}
        </ul>
      </section>

      <section className="ws-card glass" data-testid="security-admin-table">
        <div className="ws-card-head">
          <h2>
            <Crown size={15} /> Privileged role holders <span className="count">{sec.admins.length}</span>
          </h2>
          <div className="pills compact" role="group" aria-label="Filter admins">
            {[
              ["all", "All"],
              ["global", "Global Admins"],
              ["nomfa", "No MFA"],
              ["nop2", "No Entra P2"],
            ].map(([k, label]) => (
              <button key={k} type="button" className="pill" aria-pressed={adminFilter === k} onClick={() => setAdminFilter(k)} data-testid={`admins-filter-${k}`}>
                {label}
              </button>
            ))}
          </div>
        </div>
        {!sec.available.roles && (
          <div className="ws-alert">
            <Info size={13} /> Roles not read: {sec.errors.roles || "grant Directory.Read.All"}.
          </div>
        )}
        {sec.available.roles && admins.length === 0 && <p className="muted">No admins match this filter.</p>}
        {admins.length > 0 && (
          <div className="ws-table-wrap">
            <table className="ws-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Roles</th>
                  <th>MFA</th>
                  <th>Entra ID</th>
                  <th>Licenses</th>
                  <th>Last sign-in</th>
                </tr>
              </thead>
              <tbody>
                {admins.map((a) => (
                  <tr key={a.graph_id} data-testid={`admin-row-${a.upn}`}>
                    <td>
                      <div className="sku-name">
                        {a.display_name} {a.global_admin && <Crown size={11} className="warn" />}
                        {!a.enabled && <span className="mini-chip">disabled</span>}
                        {a.guest && <span className="mini-chip warn">guest</span>}
                      </div>
                      <div className="sku-part mono">{a.upn}</div>
                    </td>
                    <td>
                      <div className="lic-chips">
                        {a.roles.map((r) => (
                          <span key={r} className={`mini-chip ${r === "Global Administrator" ? "warn" : ""}`}>
                            {r}
                          </span>
                        ))}
                      </div>
                    </td>
                    <td>{a.mfa_registered === null ? <span className="muted">n/a</span> : a.mfa_registered ? <span className="mini-chip ok">registered</span> : <span className="mini-chip crit" data-testid={`admin-nomfa-${a.upn}`}>not registered</span>}</td>
                    <td>{a.entra_p2 ? <span className="mini-chip ok">P2</span> : a.entra_p1 ? <span className="mini-chip warn">P1 only</span> : <span className="mini-chip warn">none</span>}</td>
                    <td className="muted small">{a.licenses.join(", ") || "—"}</td>
                    <td className="muted small">{fmt(a.last_sign_in)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>

      <div className="two-col">
        <section className="ws-card glass" data-testid="security-legacy-table">
          <h2>Legacy authentication</h2>
          {!sec.legacy_auth && (
            <div className="ws-alert">
              <Info size={13} /> {sec.errors.signins || "Sign-in logs need AuditLog.Read.All and an Entra ID P1 license in the tenant."}
            </div>
          )}
          {sec.legacy_auth && sec.legacy_auth.sign_ins === 0 && <p className="muted ok">No basic-auth sign-ins in the last {sec.legacy_auth.window_days} days.</p>}
          {sec.legacy_auth && sec.legacy_auth.sign_ins > 0 && (
            <>
              <div className="lic-chips" style={{ marginBottom: 10 }}>
                {Object.entries(sec.legacy_auth.by_app).map(([app, n]) => (
                  <span key={app} className="mini-chip warn">
                    {app} · {n}
                  </span>
                ))}
                {sec.legacy_auth.capped && <span className="mini-chip">log window capped</span>}
              </div>
              <div className="ws-table-wrap">
                <table className="ws-table">
                  <thead>
                    <tr>
                      <th>User</th>
                      <th className="num">Sign-ins</th>
                      <th>Protocols</th>
                      <th>Last</th>
                    </tr>
                  </thead>
                  <tbody>
                    {sec.legacy_auth.top_users.map((u) => (
                      <tr key={u.upn}>
                        <td>
                          <div className="sku-name">{u.display_name}</div>
                          <div className="sku-part mono">{u.upn}</div>
                        </td>
                        <td className="num mono">
                          {u.count} <span className="muted small">({u.successful} ok)</span>
                        </td>
                        <td className="muted small">{u.apps.join(", ")}</td>
                        <td className="muted small">{fmt(u.last)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </section>
        <section className="ws-card glass" data-testid="security-mfa-card">
          <h2>MFA registration</h2>
          {!sec.mfa && (
            <div className="ws-alert">
              <Info size={13} /> {sec.errors.registration || "The registration report needs AuditLog.Read.All."}
            </div>
          )}
          {sec.mfa && (
            <>
              <div className="mfa-bar" aria-hidden="true">
                <span style={{ width: `${sec.mfa.coverage || 0}%` }} />
              </div>
              <p className="muted">
                {sec.mfa.registered.toLocaleString()} of {sec.mfa.total.toLocaleString()} active members have registered an MFA method ({sec.mfa.capable.toLocaleString()} MFA-capable). {sec.mfa.not_registered.toLocaleString()} still need a registration campaign.
              </p>
              {onShowUsers && sec.mfa.not_registered > 0 && (
                <button type="button" className="tb-btn sm" onClick={onShowUsers} data-testid="security-show-nomfa">
                  Show users without MFA <ArrowRight size={12} />
                </button>
              )}
            </>
          )}
        </section>
      </div>
      <UserDrillSheet scanId={scanId} summary={summary} drill={drill} onClose={() => setDrill(null)} />
    </div>
  );
}
